import React, { useState, useEffect } from 'react';
import { useBusinessAuth } from '../../contexts/BusinessAuthContext';
import './Analytics.css';

const Analytics = () => {
  const { token } = useBusinessAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:10000';

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await fetch(`${API_URL}/api/daycare/analytics`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch analytics');
      }

      const data = await response.json();
      setAnalytics(data);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (cents) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const formatMonth = (monthKey) => {
    const [year, month] = monthKey.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="analytics-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="analytics-container">
        <div className="error-state">
          <p>{error}</p>
          <button onClick={fetchAnalytics} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="analytics-container">
      <div className="analytics-header">
        <h1>Analytics Dashboard</h1>
        <button onClick={fetchAnalytics} className="refresh-button">
          Refresh
        </button>
      </div>

      {analytics && (
        <>
          {/* Overview Cards */}
          <div className="overview-section">
            <h2>Overview</h2>
            <div className="metrics-grid">
              <div className="metric-card revenue">
                <div className="metric-icon">💰</div>
                <div className="metric-content">
                  <div className="metric-value">
                    {formatCurrency(analytics.overview.totalRevenue)}
                  </div>
                  <div className="metric-label">Total Revenue</div>
                </div>
              </div>

              <div className="metric-card customers">
                <div className="metric-icon">👥</div>
                <div className="metric-content">
                  <div className="metric-value">
                    {analytics.overview.totalCustomers}
                  </div>
                  <div className="metric-label">Total Customers</div>
                </div>
              </div>

              <div className="metric-card videos-created">
                <div className="metric-icon">🎬</div>
                <div className="metric-content">
                  <div className="metric-value">
                    {analytics.overview.totalVideosCreated}
                  </div>
                  <div className="metric-label">Videos Created</div>
                </div>
              </div>

              <div className="metric-card credits">
                <div className="metric-icon">🎟️</div>
                <div className="metric-content">
                  <div className="metric-value">
                    {analytics.overview.creditsRemaining}
                  </div>
                  <div className="metric-label">Credits Remaining</div>
                </div>
              </div>

              <div className="metric-card average-order">
                <div className="metric-icon">📊</div>
                <div className="metric-content">
                  <div className="metric-value">
                    {formatCurrency(analytics.overview.averageOrderValue)}
                  </div>
                  <div className="metric-label">Avg Order Value</div>
                </div>
              </div>

              <div className="metric-card videos-purchased">
                <div className="metric-icon">🛒</div>
                <div className="metric-content">
                  <div className="metric-value">
                    {analytics.overview.totalVideosPurchased}
                  </div>
                  <div className="metric-label">Videos Purchased</div>
                </div>
              </div>
            </div>
          </div>

          {/* Video Status */}
          <div className="video-status-section">
            <h2>Video Status</h2>
            <div className="status-grid">
              <div className="status-card completed">
                <div className="status-count">{analytics.videos.byStatus.completed}</div>
                <div className="status-label">Completed</div>
              </div>
              <div className="status-card processing">
                <div className="status-count">{analytics.videos.byStatus.processing}</div>
                <div className="status-label">Processing</div>
              </div>
              <div className="status-card failed">
                <div className="status-count">{analytics.videos.byStatus.failed}</div>
                <div className="status-label">Failed</div>
              </div>
              <div className="status-card total">
                <div className="status-count">{analytics.videos.total}</div>
                <div className="status-label">Total Videos</div>
              </div>
            </div>
          </div>

          {/* Popular Products */}
          <div className="products-section">
            <h2>Popular Products</h2>
            <div className="products-list">
              {Object.entries(analytics.products.popularity).length > 0 ? (
                Object.entries(analytics.products.popularity)
                  .sort((a, b) => b[1] - a[1])
                  .map(([productType, count]) => (
                    <div key={productType} className="product-item">
                      <div className="product-name">
                        {productType.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
                      </div>
                      <div className="product-count">{count} purchases</div>
                    </div>
                  ))
              ) : (
                <p className="no-data">No product data yet</p>
              )}
            </div>
          </div>

          {/* Revenue Trend */}
          <div className="revenue-section">
            <h2>Revenue by Month (Last 6 Months)</h2>
            <div className="revenue-chart">
              {Object.entries(analytics.revenue.byMonth).length > 0 ? (
                <div className="chart-bars">
                  {Object.entries(analytics.revenue.byMonth)
                    .sort((a, b) => a[0].localeCompare(b[0]))
                    .map(([month, amount]) => {
                      const maxRevenue = Math.max(...Object.values(analytics.revenue.byMonth));
                      const heightPercent = maxRevenue > 0 ? (amount / maxRevenue) * 100 : 0;

                      return (
                        <div key={month} className="chart-bar-container">
                          <div className="chart-bar-wrapper">
                            <div
                              className="chart-bar"
                              style={{ height: `${heightPercent}%` }}
                            >
                              <div className="bar-value">{formatCurrency(amount)}</div>
                            </div>
                          </div>
                          <div className="chart-label">{formatMonth(month)}</div>
                        </div>
                      );
                    })}
                </div>
              ) : (
                <p className="no-data">No revenue data yet</p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Analytics;
